import type { MemberRole } from '../../domain/access'
import type { RevealSecret } from '../../domain/reveal'
import type { LocalEntity } from '../../domain/sync'
import type { CaravanDb } from './db'
import { LocalRepository } from './repository'

export type AgendaRevealRecord = {
  id: string
  tripId: string
  publicTeaser: string
  scheduledFor: string
  state: 'scheduled' | 'revealed'
  revealedAt: string | null
  authorizedSecret: RevealSecret | null
  revealRequested: boolean
  authoritativeServerTime: string | null
  snapshotReceivedAtDevice: string | null
  syncState: LocalEntity['syncState']
}

function fromEntity(entity: LocalEntity): AgendaRevealRecord {
  const payload = entity.payload
  return {
    id: entity.id,
    tripId: entity.tripId,
    publicTeaser: typeof payload.publicTeaser === 'string' ? payload.publicTeaser : '',
    scheduledFor: typeof payload.scheduledFor === 'string' ? payload.scheduledFor : entity.createdAtDevice,
    state: payload.state === 'revealed' ? 'revealed' : 'scheduled',
    revealedAt: typeof payload.revealedAt === 'string' ? payload.revealedAt : null,
    authorizedSecret: payload.authorizedSecret && typeof payload.authorizedSecret === 'object'
      ? payload.authorizedSecret as RevealSecret
      : null,
    revealRequested: typeof payload.revealRequestedAt === 'string',
    authoritativeServerTime: typeof payload.authoritativeServerTime === 'string' ? payload.authoritativeServerTime : null,
    snapshotReceivedAtDevice: typeof payload.snapshotReceivedAtDevice === 'string' ? payload.snapshotReceivedAtDevice : null,
    syncState: entity.syncState,
  }
}

export class AgendaRevealRepository {
  readonly #db: CaravanDb
  readonly #local: LocalRepository

  constructor(db: CaravanDb) {
    this.#db = db
    this.#local = new LocalRepository(db)
  }

  async list(): Promise<AgendaRevealRecord[]> {
    const entities = await this.#db.entities.where('entityType').equals('reveal')
      .filter((entity) => entity.deletedAt === null && entity.payload.kind === 'agenda_surprise')
      .toArray()
    return entities.map(fromEntity).sort((left, right) => left.scheduledFor.localeCompare(right.scheduledFor))
  }

  async prepare(input: {
    tripId: string
    publicTeaser: string
    scheduledFor: string
    secret: RevealSecret
  }): Promise<AgendaRevealRecord> {
    const publicTeaser = input.publicTeaser.trim()
    if (!publicTeaser || !input.secret.title.trim()) throw new Error('La surprise doit avoir une accroche et un titre.')
    const id = crypto.randomUUID()
    const now = new Date().toISOString()
    const entity = await this.#local.mutate({
      mutationId: crypto.randomUUID(), entityType: 'reveal', entityId: id, tripId: input.tripId,
      operation: 'create', baseVersion: 0, createdAtDevice: now,
      payload: {
        kind: 'agenda_surprise', state: 'scheduled', publicTeaser,
        scheduledFor: input.scheduledFor, revealedAt: null,
        authorizedSecret: { ...input.secret, title: input.secret.title.trim() },
      },
    })
    return fromEntity(entity)
  }

  async revealNow(id: string, role: MemberRole): Promise<AgendaRevealRecord> {
    if (role !== 'admin') throw new Error('Seul l’administrateur peut révéler une surprise.')
    const existing = await this.#db.entities.get(`reveal:${id}`)
    if (!existing) throw new Error('Surprise introuvable.')
    const revealRequestedAt = new Date().toISOString()
    const entity = await this.#local.mutate({
      mutationId: crypto.randomUUID(), entityType: 'reveal', entityId: id, tripId: existing.tripId,
      operation: 'update', baseVersion: existing.version, createdAtDevice: revealRequestedAt,
      payload: { ...existing.payload, revealRequestedAt },
    })
    return fromEntity(entity)
  }
}
